import { useCallback } from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import Image from 'components/Common/Image'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CartPanelAccommodationTileLayout from './CartPanelAccommodationTileLayout'
import useCartPanelHotelOfferData from '../hooks/useCartPanelHotelOfferData'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'

const RoomCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.three};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(10)} ${rem(12)};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(12)};
  align-self: flex-start;
  width: fit-content;
  min-width: min(${rem(300)}, 100%);
  max-width: 100%;
  container-type: inline-size;
  container-name: room-card;
`

const ChangeRoomButtonWrapper = styled.div`
  @container room-card (max-width: ${rem(360)}) {
    flex-basis: 100%;
    align-self: flex-start;
  }
`

const RoomImageWrapper = styled.div`
  width: ${rem(80)};
  height: ${rem(80)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  flex-shrink: 0;
`

const RoomCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onAdjustDays: (item: CartPanelAccommodationItem) => void
  onChangeRoom: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelDurationUnavailableTile({
  item,
  onToggleInclude,
  onAdjustDays,
  onChangeRoom,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()
  const { pkg } = useCartPanelHotelOfferData(item)

  const handleAdjustDays = useCallback(() => {
    onAdjustDays(item)
  }, [onAdjustDays, item])

  const handleChangeRoom = useCallback(() => {
    onChangeRoom(item)
  }, [onChangeRoom, item])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const durationLabel = item.duration
    ? intl.formatMessage(
        {
          id: 'vzY1Nv2NAQwUIY-kfaRz',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} not available',
          description: 'Attention indicator showing the current stay duration has no matching hotel rate',
        },
        { nights: item.duration },
      )
    : intl.formatMessage({
        id: 'Xmzbp8R31R0qY7HhjltT',
        defaultMessage: 'Duration not available',
        description: 'Attention indicator when no hotel rate matches the itinerary duration',
      })

  const availableLabel = item.availableDurations?.length
    ? intl.formatMessage(
        {
          id: '-H2Q6-Pr1gjIUdXm3JaQ',
          defaultMessage: 'Available: {durations}',
          description: 'Shows which night counts are available for a hotel when the current duration has no rate',
        },
        {
          durations: item.availableDurations.map(d => intl.formatMessage(
            { id: 'AsP3VSrHhSASl5Qz8Mji', defaultMessage: '{count, plural, one {# night} other {# nights}}', description: 'Night count label for showing available hotel stay durations in the cart panel' },
            { count: d },
          )).join(', '),
        },
      )
    : undefined

  const roomImage = pkg?.roomType.images[0]

  const header = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading4">{item.name}</Heading>
      {item.dates && <BodyText variant="medium" colour="neutral-three">{item.dates}</BodyText>}
    </Group>
  )

  const roomCard = (item.roomName || item.occupancy) ? (
    <RoomCard>
      {roomImage && (
        <RoomImageWrapper>
          <Image image={roomImage} width={80} height={80} fit="center" objectFit="cover" dpr={2} />
        </RoomImageWrapper>
      )}
      <RoomCardContent>
        {item.roomName && <BodyText variant="medium" weight="bold">{item.roomName}</BodyText>}
        {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
      </RoomCardContent>
      <ChangeRoomButtonWrapper>
        <TextButton kind="tertiary" size="small" startIcon={<LinePenIcon />} onClick={handleChangeRoom} disabled={isStreaming}>
          {intl.formatMessage({
            id: 'BrfTYdJonj0A3hETLQR8',
            defaultMessage: 'Change room',
            description: 'Link to change the selected room for a hotel in the cart panel',
          })}
        </TextButton>
      </ChangeRoomButtonWrapper>
    </RoomCard>
  ) : undefined

  const attention = (
    <Group direction="vertical" gap={4}>
      <MessagingLabel variant="warningLight">
        {durationLabel}
      </MessagingLabel>
      {availableLabel && <BodyText variant="small" colour="neutral-three">{availableLabel}</BodyText>}
      <TextButton kind="tertiary" size="small" onClick={handleAdjustDays} disabled={isStreaming}>
        {intl.formatMessage({
          id: 'ZayCP4wyMYOsR09FUgqL',
          defaultMessage: 'Adjust days',
          description: 'Action button to adjust hotel stay duration from the cart panel',
        })}
      </TextButton>
    </Group>
  )

  const deleteAction = (
    <TextButton kind="secondary" size="small" shape="round" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
      {intl.formatMessage({
        id: 'jDEZjhV3eo-cnxIBHOtR',
        defaultMessage: 'Delete',
        description: 'Button to permanently delete an item from the trip planner cart panel',
      })}
    </TextButton>
  )

  const price = item.pricing.price ? (
    <PriceRowPrice
      price={item.pricing.price}
      priceFormat="preciseDollar"
      saleUnit=""
      size="S"
    />
  ) : undefined

  return (
    <CartPanelAccommodationTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      header={header}
      roomCard={roomCard}
      attention={attention}
      deleteAction={deleteAction}
      price={price}
    />
  )
}

export default CartPanelDurationUnavailableTile
